// src/components/connexion/couleursConnexion.js
// ----------------------------------------------------------------------------
// LES COULEURS DE LA PAGE DE CONNEXION DU PERSONNEL
// ----------------------------------------------------------------------------
// Elles se règlent depuis le CIK (« Page de connexion KLASYS » → Couleurs), au
// même titre que les textes, et se rangent dans `klasys_tenants.page_connexion`,
// clé `connexion`, bloc `couleurs`. C'est le même mécanisme que pour l'espace
// famille (`src/espace-famille/couleurs.js`).
//
// Une couleur venue du CIK ne peut pas être une classe Tailwind : celles-ci
// sont figées à la compilation. On les pose donc en variables CSS sur la racine
// de la page, et les classes les lisent (`bg-[color:var(--connexion-principale)]`).
// Les teintes translucides — bordures, anneaux de focus, voile de l'affiche —
// sont calculées ici, parce qu'une variable CSS ne se combine pas avec la
// notation `/15` de Tailwind.
//
// Les valeurs ci-dessous sont celles d'origine : tant que le CIK n'a rien
// réglé, et dès qu'on y clique « Revenir aux couleurs d'origine », ce sont
// elles qui s'appliquent.
// ----------------------------------------------------------------------------

export const COULEURS_PAR_DEFAUT = {
  principale: '#246990', // boutons, liens, bordures actives, bandeau du pied
  foncee: '#1b4f6d',     // survol des boutons
  fond: '#eaf4f7',       // fond de la page
  affiche: '#1f5f5e'     // fond du panneau illustré, à gauche
};

const EST_COULEUR = /^#[0-9a-fA-F]{6}$/;

/** La couleur si elle est lisible, sinon celle d'origine. */
const propre = (valeur, cle) => (EST_COULEUR.test(String(valeur || '')) ? valeur : COULEURS_PAR_DEFAUT[cle]);

const composantes = (hex) => {
  const n = parseInt(String(hex).slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** `#246990` + 0.15 → `rgba(36, 105, 144, 0.15)`. */
export function rgba(hex, alpha = 1) {
  const [r, v, b] = composantes(EST_COULEUR.test(String(hex || '')) ? hex : COULEURS_PAR_DEFAUT.principale);
  return `rgba(${r}, ${v}, ${b}, ${alpha})`;
}

/** Une teinte assombrie, pour le voile posé sur l'illustration. */
const assombrir = (hex, facteur) => {
  const [r, v, b] = composantes(hex);
  const sombre = (x) => Math.max(0, Math.round(x * facteur));
  return [sombre(r), sombre(v), sombre(b)];
};

/**
 * Les variables CSS à poser sur la racine de la page, et le fond de celle-ci.
 * `couleurs` vient du CIK ; ce qui manque ou n'est pas lisible reste d'origine.
 */
export function variables(couleurs = {}) {
  const c = {
    principale: propre(couleurs.principale, 'principale'),
    foncee: propre(couleurs.foncee, 'foncee'),
    fond: propre(couleurs.fond, 'fond'),
    affiche: propre(couleurs.affiche, 'affiche')
  };
  const [rv, vv, bv] = assombrir(c.affiche, 0.66);
  const voile = (alpha) => `rgba(${rv}, ${vv}, ${bv}, ${alpha})`;

  return {
    '--connexion-principale': c.principale,
    '--connexion-principale-40': rgba(c.principale, 0.4),
    '--connexion-principale-15': rgba(c.principale, 0.15),
    '--connexion-principale-10': rgba(c.principale, 0.1),
    '--connexion-foncee': c.foncee,
    '--connexion-affiche': c.affiche,
    '--connexion-fond': c.fond,
    // Teinte très pâle de la couleur principale : encadrés d'information,
    // pastille de l'année, carte de l'année choisie.
    '--connexion-pale': rgba(c.principale, 0.08),
    '--connexion-ombre': rgba(c.principale, 0.12),
    // Voile dégradé posé sur l'illustration, pour que le texte reste lisible.
    '--connexion-voile': `linear-gradient(0deg, ${voile(0.95)} 0%, ${voile(0.35)} 55%, ${voile(0)} 100%)`,
    backgroundColor: c.fond
  };
}
